import React, { useEffect, useState } from "react";
import { toast } from "@telia/teddy";
import CommandPalette, { filterItems, getItemIndex } from "react-cmdk";
import "./CommandMenu.less";
import { useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { RootState } from "../../../../../../models";
import { getOrderRef } from "../../../state/selectors";
import { copyToClipboard } from "../../../../../utils/copyToClipboard";
import CloneProdOrderModal from "./CloneProdOrderModal";
import ValidateOrderModal from "./ValidateOrderModal";

const CommandMenu = () => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [cloneProdOrderModalOpen, setCloneProdOrderModalOpen] = useState(false);
    const [validateOrderModalOpen, setValidateOrderModalOpen] = useState(false);
    const orderRef = useSelector((state: RootState) => getOrderRef(state));
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const isBankIdPage = location.pathname.includes("/personlige-opplysninger");

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if ((navigator?.platform?.toLowerCase().includes("mac") ? e.metaKey : e.ctrlKey) && e.key === "k") {
                e.preventDefault();
                e.stopPropagation();

                setOpen((currentValue) => {
                    return !currentValue;
                });
            }
        }

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const filteredItems = filterItems(
        [
            {
                heading: "Commands",
                id: "commands",
                items: [
                    ...(isBankIdPage
                        ? [
                              {
                                  id: "skip-bankid",
                                  children: "Skip BankID",
                                  icon: "CreditCardIcon" as const,
                                  href: "/shop/devtools/medlem/",
                              },
                          ]
                        : []),
                    {
                        id: "save-order",
                        children: "Save order",
                        icon: "BookmarkIcon",
                        onClick: async () => {
                            const response = await fetch("/shop/devtools/save/");
                            if (response.status === 200) {
                                toast.success("Order has been saved", {
                                    position: "top-right",
                                });
                            }
                        },
                    },
                    {
                        id: "load-order",
                        children: "Load order",
                        icon: "ArrowUturnLeftIcon",
                        onClick: async () => {
                            toast.info("Loading order...", {
                                position: "top-right",
                            });

                            await fetch("/shop/devtools/load/");
                            window.location.reload();
                        },
                    },
                    {
                        id: "validate-order",
                        children: "Validate order",
                        icon: "CheckCircleIcon" as const,
                        onClick: () => {
                            setOpen(false);
                            setValidateOrderModalOpen(true);
                        },
                    },
                    {
                        id: "clone-prod-order",
                        children: "Clone an order",
                        icon: "DocumentDuplicateIcon" as const,
                        onClick: () => {
                            setOpen(false);
                            setCloneProdOrderModalOpen(true);
                        },
                    },
                    {
                        id: "copy-link-to-order",
                        children: "Copy link to order at current page",
                        icon: "LinkIcon",
                        onClick: async () => {
                            searchParams.set("order_reference", orderRef || "");
                            const orderLink = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
                            copyToClipboard(orderLink);
                            toast.success("Link copied to clipboard", {
                                position: "top-right",
                            });
                        },
                    },
                    {
                        id: "clear-order-cookie",
                        children: "Clear order cookie",
                        icon: "TrashIcon",
                        onClick: async () => {
                            await fetch("/shop/api/v2/cart/order", { method: "DELETE" });
                            toast.success("Order cookie cleared", {
                                position: "top-right",
                            });
                        },
                    },
                    {
                        id: "fwa-test",
                        children: "Test FWA",
                        icon: "SparklesIcon" as const,
                        onClick: () => {
                            window.location.href = "/shop/devtools/fwa";
                        },
                    },

                    {
                        id: "devtools-order",
                        children: "Devtools order",
                        icon: "CodeBracketIcon",
                        onClick: () => {
                            window.open("/shop/devtools/order", "_blank");
                        },
                    },
                ],
            },
        ],
        search,
    );

    return (
        <>
            <CommandPalette onChangeSearch={setSearch} onChangeOpen={setOpen} search={search} isOpen={open} page="root">
                <CommandPalette.Page id="root">
                    {filteredItems.length ? (
                        filteredItems.map((list) => (
                            <CommandPalette.List key={list.id} heading={list.heading}>
                                {list.items.map(({ id, ...rest }) => (
                                    <CommandPalette.ListItem
                                        key={id}
                                        index={getItemIndex(filteredItems, id)}
                                        {...rest}
                                    />
                                ))}
                            </CommandPalette.List>
                        ))
                    ) : (
                        <CommandPalette.FreeSearchAction />
                    )}
                </CommandPalette.Page>
            </CommandPalette>
            <CloneProdOrderModal open={cloneProdOrderModalOpen} onOpenChange={setCloneProdOrderModalOpen} />
            <ValidateOrderModal open={validateOrderModalOpen} onOpenChange={setValidateOrderModalOpen} />
        </>
    );
};

export default CommandMenu;
